import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  CheckCircle,
  Archive,
  Trash2,
  Eye,
  MessageCircle,
  Clock,
  Reply,
} from "lucide-react";
import { contactMessageService } from "@/services/firebase";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmationModal } from "@/components/shared/ConfirmationModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import toast from "react-hot-toast";
import type { ContactMessage } from "@/types";

export function MessagesAdminPage() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [messageToDelete, setMessageToDelete] = useState<ContactMessage | null>(null);

  useEffect(() => {
    const unsubscribe = contactMessageService.subscribe(setMessages);
    return () => unsubscribe();
  }, []);

  const filtered = filter === "all" ? messages : messages.filter((m) => m.status === filter);

  const handleStatusChange = async (id: string, status: ContactMessage["status"]) => {
    try {
      await contactMessageService.updateStatus(id, status);
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Erreur");
    }
  };

  const handleDelete = (message: ContactMessage) => {
    setMessageToDelete(message);
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;
    try {
      await contactMessageService.delete(messageToDelete.id);
      toast.success("Message supprimé");
      setMessageToDelete(null);
    } catch {
      toast.error("Erreur");
    }
  };

  const statusConfig = {
    new: { label: "Nouveau", variant: "danger" as const, icon: Mail },
    read: { label: "Lu", variant: "warning" as const, icon: Eye },
    replied: { label: "Répondu", variant: "success" as const, icon: Reply },
    archived: { label: "Archivé", variant: "outline" as const, icon: Archive },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark">Messages de Contact</h1>
          <p className="text-gray-500 text-sm">{messages.length} messages reçus</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "new", "read", "replied", "archived"].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {status === "all" ? "Tous" : statusConfig[status as keyof typeof statusConfig]?.label}
            {status !== "all" && (
              <span className="ml-1 text-xs">
                ({messages.filter((m) => m.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Expéditeur</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((msg) => {
                  const status = statusConfig[msg.status];
                  return (
                    <TableRow
                      key={msg.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelected(msg)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-dark">{msg.name}</p>
                          <p className="text-xs text-gray-500">{msg.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-dark">{msg.subject || msg.message.slice(0, 50)}...</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {msg.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {msg.createdAt ? new Date(msg.createdAt?.toString()).toLocaleDateString("fr-FR") : "Inconnue"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={status?.variant || "default"}>
                          {status?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {msg.status === "new" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusChange(msg.id, "read");
                              }}
                              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                              title="Marquer comme lu"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(msg.id, "archived");
                            }}
                            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
                            title="Archiver"
                          >
                            <Archive className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(msg);
                            }}
                            className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={!!messageToDelete}
        onClose={() => setMessageToDelete(null)}
        onConfirm={confirmDelete}
        title="Supprimer le message"
        message={`Êtes-vous sûr de vouloir supprimer le message de ${messageToDelete?.name || "ce contact"} ?`}
      />

      {/* Message Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-dark mb-4">Message de {selected.name}</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Téléphone</span>
                  <span className="font-medium">{selected.phone}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{selected.email || "Inconnu"}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-500">Type</span>
                  <Badge variant="secondary" className="capitalize">{selected.type}</Badge>
                </div>
                <div className="py-2">
                  <span className="text-gray-500 block mb-2">Message</span>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selected.message}</p>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setSelected(null)}>
                  Fermer
                </Button>
                {selected.status === "new" && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      handleStatusChange(selected.id, "read");
                      setSelected(null);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marquer lu
                  </Button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
